<template>
  <!--
    Cockpit home (PI-244). Three layers from top to bottom:
      Layer 1 — Markt-pulse (built in Slice 6)
      Layer 2 — Vraagt aandacht (built in Slice 7)
      Layer 3 — Snapshot 'Mijn omgeving' + energy news
    Slice 4 wires the welcome bar, Layer-3 snapshot, and the energy news
    column. The two top layers ship as soon as their backends land.

    PI-246 — Header chrome is provided by StandardToolPage so this page
    shares the same visual frame as the tool / dashboard pages.
  -->
  <standard-tool-page
    :title="demo ? t('portal_demo.welcome') : greeting"
    :subtitle="demo ? t('portal_demo.sample') : currentTimeLabel"
    :show-back-link="false"
  >
    <template #actions>
      <tool-help-button tool-key="home" />
    </template>

    <div class="space-y-5">
      <!-- Layer 1 — Markt-pulse strip (full width). -->
      <pulse-strip :demo="demo" />

      <!--
        Layers 2+3 — Left column holds Ontdek → Notifications → Mijn omgeving
        snapshot stacked. Right column holds the energy news only
        and stays sticky at its natural height while the left column scrolls.

        Layout note (PI-244): the bottom row uses the SAME 4-column grid as
        the pulse strip above, with the left stack spanning 3 columns and
        the news column spanning 1. This guarantees the right edge of the
        4th pulse card and the left edge of the news column land on the
        exact same vertical line — a 3fr:1fr split would put them ~g/2
        apart and break the alignment.
      -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-5 items-start">
        <div class="space-y-5 min-w-0 md:col-span-3">
          <discover-card />
          <home-notifications-card :demo="demo" />
          <home-snapshot-section :demo="demo" />
        </div>
        <cockpit-news-column
          :demo="demo"
          class="md:col-span-1 md:sticky md:top-5"
        />
      </div>
    </div>
  </standard-tool-page>
</template>

<script setup>
  /* eslint-disable no-undef */
  import CockpitNewsColumn from '@/pages/home/components/CockpitNewsColumn.vue';
  import DiscoverCard from '@/pages/home/components/DiscoverCard.vue';
  import HomeNotificationsCard from '@/pages/home/components/HomeNotificationsCard.vue';
  import HomeSnapshotSection from '@/pages/home/components/HomeSnapshotSection.vue';
  import PulseStrip from '@/pages/home/components/PulseStrip.vue';
  import StandardToolPage from '@/components/ui/layout/StandardToolPage.vue';
  import ToolHelpButton from '@/components/ui/layout/ToolHelpButton.vue';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { demoEnabled } from '@/storage/demoMode';

  const authStore = useAuthStore();
  const { t } = useI18n();
  const demo = computed(() => demoEnabled.value && !authStore.isAuthenticated);

  const now = ref(new Date());
  let currentTimeTimer = null;

  const greeting = computed(() => {
    const firstName = authStore.firstname || '';
    const email = authStore.email || '';
    const name = (firstName || email.split('@')[0] || '').trim();
    return name
      ? t('home_cockpit.welcome_named', { name })
      : t('home_cockpit.welcome');
  });

  const currentTimeLabel = computed(() => {
    return t('home_cockpit.current_time', {
      time: now.value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    });
  });

  function updateCurrentTime() {
    now.value = new Date();
  }

  onMounted(() => {
    updateCurrentTime();
    currentTimeTimer = setInterval(updateCurrentTime, 30 * 1000);
  });

  onUnmounted(() => {
    if (currentTimeTimer) {
      clearInterval(currentTimeTimer);
    }
  });
</script>
